import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface CostStage {
  stage: string;
  cost: number;
  costFormatted: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  latencyMs: number;
}

interface CostBreakdownProps {
  totalCost: number;
  totalCostFormatted: string;
  totalLatencyMs: number;
  stages: CostStage[];
}

const COLORS = [
  '#d4a574', // rune
  '#f87171', // ember
  '#22c55e', // success
  '#fbbf24', // amber
  '#22d3ee', // cyan
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#84cc16', // lime
];

function formatCostUsd(value: number): string {
  if (!Number.isFinite(value)) return '$0.0000';
  if (value >= 1) return `$${value.toFixed(2)}`;
  if (value >= 0.1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(4)}`;
}

function formatDurationMs(value: number): string {
  if (value >= 60000) return `${(value / 60000).toFixed(1)}m`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}s`;
  return `${value}ms`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function CostBreakdown({
  totalCost,
  totalCostFormatted,
  totalLatencyMs,
  stages,
}: CostBreakdownProps) {
  const pieData = stages.map((s) => ({
    name: s.stage,
    value: s.cost,
  }));

  const totalTokens = stages.reduce((sum, s) => sum + s.inputTokens + s.outputTokens, 0);

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg text-parchment">Cost Breakdown</h3>
        <span className="text-xl font-display text-parchment">{totalCostFormatted}</span>
      </div>

      {/* KPI boxes */}
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="bg-void/60 rounded-lg p-3">
          <div className="text-mist/70 text-xs">Total Cost</div>
          <div className="text-parchment text-lg font-medium">{formatCostUsd(totalCost)}</div>
        </div>
        <div className="bg-void/60 rounded-lg p-3">
          <div className="text-mist/70 text-xs">Total Tokens</div>
          <div className="text-parchment text-lg font-medium">{formatNumber(totalTokens)}</div>
        </div>
        <div className="bg-void/60 rounded-lg p-3">
          <div className="text-mist/70 text-xs">Total Latency</div>
          <div className="text-parchment text-lg font-medium">{formatDurationMs(totalLatencyMs)}</div>
        </div>
      </div>

      {/* Pie Chart and Legend */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="w-40 h-40 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={60}
                paddingAngle={2}
                dataKey="value"
              >
                {pieData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                formatter={(value: number) => formatCostUsd(value)}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Stage breakdown */}
        <div className="flex-1 space-y-2 w-full">
          {stages.map((stage, i) => {
            const percentage = totalCost > 0 ? (stage.cost / totalCost) * 100 : 0;
            return (
              <div
                key={stage.stage}
                className="flex items-center gap-3 p-2 bg-void/40 rounded-lg text-xs"
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: COLORS[i % COLORS.length] }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-parchment font-medium truncate">{stage.stage}</span>
                    <span className="text-mist">{formatCostUsd(stage.cost)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-mist/70 mt-0.5">
                    <span className="truncate">{stage.model}</span>
                    <span>{percentage.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stacked bar */}
      {stages.length > 0 && totalCost > 0 && (
        <div className="h-4 rounded-full overflow-hidden flex">
          {stages.map((stage, i) => {
            const percentage = (stage.cost / totalCost) * 100;
            return (
              <div
                key={stage.stage}
                className="h-full first:rounded-l-full last:rounded-r-full"
                style={{
                  width: `${percentage}%`,
                  backgroundColor: COLORS[i % COLORS.length],
                }}
                title={`${stage.stage}: ${formatCostUsd(stage.cost)}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
